import { useState } from 'react';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Alert,
  CircularProgress,
  Stack
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import PasswordField from '../../components/common/PasswordField';
import PasswordStrength from '../../components/auth/PasswordStrength';
import authService from '../../services/authService';
import { validators } from '../../utils/validators';
import alertify from 'alertifyjs';

/**
 * ResetPasswordPage - Reset password with token
 * 
 * User clicks email link → Enters new password → Password reset
 */
const ResetPasswordPage = () => {
  const { token } = useParams();
  const history = useHistory();
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Clear error on change
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate new password
    const passwordError = validators.password(formData.newPassword);
    if (passwordError) {
      newErrors.newPassword = passwordError;
    }

    // Validate password match
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Şifreler eşleşmiyor';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      alertify.error('Lütfen tüm alanları doğru şekilde doldurun');
      return;
    }

    setLoading(true);

    try {
      const response = await authService.resetPassword(token, formData.newPassword);
      
      alertify.success(response.message || 'Şifreniz başarıyla değiştirildi');
      setResetSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        history.push('/login');
      }, 3000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Şifre sıfırlama başarısız. Link geçersiz veya süresi dolmuş olabilir.';
      alertify.error(errorMessage);
      
      // If token is invalid, show error
      if (err.response?.status === 400) {
        setErrors({ token: errorMessage });
      }
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (resetSuccess) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'grey.100',
          py: 4
        }}
      >
        <Container maxWidth="sm">
          <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
            <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main', mb: 3 }} />
            
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Şifre Başarıyla Değiştirildi! ✅
            </Typography>
            
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Yeni şifrenizle giriş yapabilirsiniz.
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              3 saniye içinde giriş sayfasına yönlendirileceksiniz...
            </Alert>
            
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => history.push('/login')}
              fullWidth
            >
              Hemen Giriş Yap
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Reset form
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.100',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <LockResetIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
              Yeni Şifre Oluştur
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Güçlü bir şifre seçin
            </Typography>
          </Box>

          {/* Token Error Alert */}
          {errors.token && (
            <Alert severity="error" sx={{ mb: 3 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {errors.token}
              </Typography>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Button variant="outlined" size="small" sx={{ mt: 1 }}>
                  Yeni şifre sıfırlama linki iste
                </Button>
              </Link>
            </Alert>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <Alert severity="info">
                Şifreniz en az 8 karakter, 1 büyük harf ve 1 rakam içermelidir.
              </Alert>

              <PasswordField
                label="Yeni Şifre"
                name="newPassword"
                value={formData.newPassword}
                onChange={handleChange}
                error={!!errors.newPassword}
                helperText={errors.newPassword}
                disabled={loading}
                autoComplete="new-password"
                autoFocus
                required
              />

              {/* Password Strength Indicator */}
              <PasswordStrength password={formData.newPassword} />

              <PasswordField
                label="Yeni Şifre (Tekrar)"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                error={!!errors.confirmPassword}
                helperText={errors.confirmPassword}
                disabled={loading}
                autoComplete="new-password"
                required
              />

              <Button
                fullWidth
                type="submit"
                variant="contained"
                size="large"
                disabled={loading || !formData.newPassword || !formData.confirmPassword}
                startIcon={loading ? <CircularProgress size={20} /> : <LockResetIcon />}
                sx={{ mt: 3, py: 1.5 }}
              >
                {loading ? 'Şifre Değiştiriliyor...' : 'Şifremi Değiştir'}
              </Button>
            </Stack>
          </form>

          {/* Back to Login */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Şifrenizi hatırladınız mı?{' '}
              <Link to="/login" style={{ textDecoration: 'none', fontWeight: 600 }}>
                Giriş Yapın
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;

